import { notificationStyle } from "@/lib/notifications/notification-style";
import {
  vehicleEventSeverity,
  type ParsedVehicleEvent,
  type VehicleEventType,
} from "@/lib/notifications/vehicle-events";

/**
 * What each vehicle is "under" right now, for the live map badge, the followed-vehicle callout and
 * the vehicle list.
 *
 * Upstream sends edges, not state (`docs/Events.MD`), so paired events are folded back into
 * *conditions* here — inside a no-go zone from `nogo_zone_enter` until `nogo_zone_exit`, and so on
 * — while everything else is a *transient* that shows for {@link TRANSIENT_MS}.
 *
 * Fed with every event, before the toast-preference filter: a closing event must close its
 * condition even when the user switched that closing type off. Preferences are applied on read
 * (`useVehicleAlerts`). Memory only, and cleared on logout — after a reload a condition reappears
 * with the next event that opens it.
 *
 * The snapshot changes identity only when an alert opens, closes or expires, never on ordinary
 * telemetry, so the 400 ms LiveTracking flush does not re-render the memoized children.
 */

export interface VehicleAlert {
  /** The event type that raised it — the opening one, for a condition. */
  kind: VehicleEventType;
  /** Zone id, driver ref or speed limit — see `ParsedVehicleEvent.arg`. */
  arg: string | null;
  /** Epoch ms. */
  since: number;
  transient: boolean;
}

export const TRANSIENT_MS = 60_000;

/** Opening type → the type that closes it. */
const CONDITION_CLOSERS: Record<string, VehicleEventType> = {
  nogo_zone_enter: "nogo_zone_exit",
  go_zone_exit: "go_zone_enter",
  speeding_start: "speeding_end",
  street_speeding_start: "street_speeding_end",
};

/** Closing type → the type it closes. */
const CONDITION_OPENERS: Record<string, VehicleEventType> = Object.fromEntries(
  Object.entries(CONDITION_CLOSERS).map(([open, close]) => [close, open]),
);

/** Zone conditions are per zone; a speeding condition is one per vehicle, whatever its limit. */
const ZONE_CONDITIONS = new Set<VehicleEventType>(["nogo_zone_enter", "go_zone_exit"]);

function conditionKey(openType: VehicleEventType, arg: string | null): string {
  return ZONE_CONDITIONS.has(openType) ? `${openType}:${arg ?? ""}` : openType;
}

interface VehicleState {
  conditions: Map<string, VehicleAlert>;
  transient: VehicleAlert | null;
  transientTimer: ReturnType<typeof setTimeout> | null;
}

const EMPTY: ReadonlyMap<string, readonly VehicleAlert[]> = new Map();

const states = new Map<string, VehicleState>();
let snapshot: ReadonlyMap<string, readonly VehicleAlert[]> = EMPTY;
const listeners = new Set<() => void>();

function rebuild() {
  const next = new Map<string, readonly VehicleAlert[]>();
  for (const [vehicleId, state] of states) {
    const alerts = [...state.conditions.values()];
    if (state.transient) alerts.push(state.transient);
    if (alerts.length > 0) next.set(vehicleId, alerts);
  }
  snapshot = next;
  for (const listener of [...listeners]) listener();
}

function stateFor(vehicleId: string): VehicleState {
  let state = states.get(vehicleId);
  if (!state) {
    state = { conditions: new Map(), transient: null, transientTimer: null };
    states.set(vehicleId, state);
  }
  return state;
}

function dropIfEmpty(vehicleId: string, state: VehicleState) {
  if (state.conditions.size === 0 && !state.transient) states.delete(vehicleId);
}

function setTransient(vehicleId: string, state: VehicleState, alert: VehicleAlert) {
  if (state.transientTimer) clearTimeout(state.transientTimer);
  state.transient = alert;
  state.transientTimer = setTimeout(() => {
    // Only this vehicle's own entry, and only if nothing newer replaced it meanwhile.
    const current = states.get(vehicleId);
    if (!current || current.transient !== alert) return;
    current.transient = null;
    current.transientTimer = null;
    dropIfEmpty(vehicleId, current);
    rebuild();
  }, TRANSIENT_MS);
}

export interface VehicleAlertPoint {
  /** The update's `is_speeding`; `false` closes a vehicle-limit speeding condition. */
  isSpeeding?: unknown;
  /** The update's `is_street_speeding`; `false` closes a road-limit speeding condition. */
  isStreetSpeeding?: unknown;
  /** Speed (km/h) on the point; the fallback when a flag is missing. */
  speed?: number;
  /** Epoch ms of the point. */
  at: number;
}

/** The speeding conditions and the end event each one stands for when it is healed. */
const SPEEDING_ENDS = {
  speeding_start: "speeding_end",
  street_speeding_start: "street_speeding_end",
} as const;

type SpeedingStart = keyof typeof SPEEDING_ENDS;

/** The flags' wire type is undocumented, so `0` and `"0"` count as "not speeding" too. */
function isFalseFlag(value: unknown): boolean {
  return value === false || value === 0 || value === "0" || value === "false";
}

/**
 * Whether the point shows the vehicle is no longer over the limit that opened `condition`.
 * The flag decides when it is present. Without it, a vehicle limit (the opening event's arg) can be
 * compared with the speed, but a road limit changes from road to road, so only a standstill counts.
 */
function speedingIsOver(
  kind: SpeedingStart,
  condition: VehicleAlert,
  point: VehicleAlertPoint,
  stopped: boolean,
): boolean {
  if (stopped || point.speed === 0) return true;
  const flag = kind === "speeding_start" ? point.isSpeeding : point.isStreetSpeeding;
  if (flag !== undefined && flag !== null) return isFalseFlag(flag);
  if (kind === "street_speeding_start" || point.speed === undefined) return false;
  const limit = Number(condition.arg);
  return Number.isFinite(limit) && limit > 0 && point.speed <= limit;
}

/**
 * Folds one update's events into the vehicle's state. Call it for every update, including those
 * with no events: upstream sometimes never sends a `*_end`, so an open speeding condition is also
 * closed by the point itself (its flags, its speed, or a stop).
 *
 * Returns the end events that healing stood in for, so the caller can toast them as if upstream
 * had sent them. A condition closed by a real `*_end` returns nothing; that event is toasted anyway.
 */
export function recordVehicleUpdate(
  vehicleId: string,
  events: readonly ParsedVehicleEvent[],
  point: VehicleAlertPoint,
): ParsedVehicleEvent[] {
  const existing = states.get(vehicleId);
  const isSpeedingNow =
    existing?.conditions.has("speeding_start") || existing?.conditions.has("street_speeding_start");
  if (events.length === 0 && !isSpeedingNow) return [];

  const state = existing ?? stateFor(vehicleId);
  let changed = false;
  /** Opened on this very point; a flag on the same point cannot also have closed it. */
  const openedNow = new Set<string>();

  for (const event of events) {
    const closes = CONDITION_OPENERS[event.type];
    if (CONDITION_CLOSERS[event.type]) {
      openedNow.add(conditionKey(event.type, event.arg));
      state.conditions.set(conditionKey(event.type, event.arg), {
        kind: event.type,
        arg: event.arg,
        since: point.at,
        transient: false,
      });
    } else if (closes) {
      state.conditions.delete(conditionKey(closes, event.arg));
      // The "back under the limit" / "left the zone" moment itself is still worth a glance.
      setTransient(vehicleId, state, { kind: event.type, arg: event.arg, since: point.at, transient: true });
    } else {
      setTransient(vehicleId, state, { kind: event.type, arg: event.arg, since: point.at, transient: true });
    }
    changed = true;
  }

  const healed: ParsedVehicleEvent[] = [];
  const stopped = events.some((event) => event.type === "stopped");
  for (const kind of Object.keys(SPEEDING_ENDS) as SpeedingStart[]) {
    const condition = state.conditions.get(kind);
    if (!condition || openedNow.has(kind) || !speedingIsOver(kind, condition, point, stopped)) continue;
    state.conditions.delete(kind);
    const end: ParsedVehicleEvent = { type: SPEEDING_ENDS[kind], arg: condition.arg };
    setTransient(vehicleId, state, { kind: end.type, arg: end.arg, since: point.at, transient: true });
    healed.push(end);
    changed = true;
  }

  dropIfEmpty(vehicleId, state);
  if (changed) rebuild();
  return healed;
}

export function clearVehicleAlerts() {
  for (const state of states.values()) {
    if (state.transientTimer) clearTimeout(state.transientTimer);
  }
  states.clear();
  if (snapshot !== EMPTY) {
    snapshot = EMPTY;
    for (const listener of [...listeners]) listener();
  }
}

export function subscribeVehicleAlerts(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getVehicleAlertsSnapshot(): ReadonlyMap<string, readonly VehicleAlert[]> {
  return snapshot;
}

export function getVehicleAlertsServerSnapshot(): ReadonlyMap<string, readonly VehicleAlert[]> {
  return EMPTY;
}

const TONE_RANK = { destructive: 0, warning: 1, driver: 2, primary: 3, success: 4 } as const;

/**
 * Most important first: open conditions before transients, then by colour (red before amber before
 * the rest), then newest. The first entry is what the marker badge shows.
 */
export function sortVehicleAlerts(alerts: readonly VehicleAlert[]): VehicleAlert[] {
  return [...alerts].sort((a, b) => {
    if (a.transient !== b.transient) return a.transient ? 1 : -1;
    const toneA = TONE_RANK[notificationStyle(a.kind, vehicleEventSeverity(a.kind)).tone];
    const toneB = TONE_RANK[notificationStyle(b.kind, vehicleEventSeverity(b.kind)).tone];
    if (toneA !== toneB) return toneA - toneB;
    return b.since - a.since;
  });
}
